// src/pages/SelectionOrders/subpages/SelectionOrderShow.tsx
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumbs, { BreadcrumbsItem } from '../../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../../components/default/DefaultCard';
import LazyLoading from '../../../components/LazyLoading';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { Tab, Tabs } from 'react-bootstrap';
import { toast } from 'react-toastify';

import useSelectionOrderService from '../../../app/services/hooks/useSelectionOrderService';
import { AppContext } from '../../../contexts/AppContext';

import {
  SelectionOrder,
  SelectionOrderBatchRow,
  SelectionOrderCollectorRow,
  SelectionOrderDestinationBatchRow,
} from '../../../app/models/SelectionOrder';

import SelectionOrderHeaderForm from '../components/forms/SelectionOrderHeaderForm';
import OrderBatchesTab from '../components/tables/OrderBatchesTab';
import OrderCollectorsTab from '../components/tables/OrderCollectorsTab';

type RouterParams = { id: string };

const SelectionOrdersShow: React.FC = () => {
  const { id } = useParams<RouterParams>();
  const orderId = Number(id);

  const { showLoading, hideLoading } = useContext(AppContext);
  const { showSelectionOrder, fetchingShowSelectionOrder } = useSelectionOrderService();

  // Refs para evitar bucles
  const showRef = useRef(showSelectionOrder);
  const showLoadingRef = useRef(showLoading);
  const hideLoadingRef = useRef(hideLoading);
  useEffect(() => { showRef.current = showSelectionOrder; }, [showSelectionOrder]);
  useEffect(() => { showLoadingRef.current = showLoading; }, [showLoading]);
  useEffect(() => { hideLoadingRef.current = hideLoading; }, [hideLoading]);

  const [verifying, setVerifying] = useState<boolean>(true);
  const [order, setOrder] = useState<SelectionOrder>({
    status: 'CREATED',
    is_active: true,
    collectors: [],
    batches: [],
    pending_batches: [],
    destination_batches: [],
  });

  const load = useCallback((oid: number) => {
    showLoadingRef.current?.('loading', 'Cargando orden...');
    showRef.current(oid, {
      onSuccess: (resp: ServiceResponse) => {
        const o = resp.data.selection_order as SelectionOrder;
        setOrder({
          ...o,
          collectors: o.collectors ?? [],
          batches: o.batches ?? [],
          pending_batches: o.pending_batches ?? [],
          destination_batches: o.destination_batches ?? [],
        });
        hideLoadingRef.current?.();
        setVerifying(false);
      },
      onError: (resp: ServiceResponse) => {
        hideLoadingRef.current?.();
        toast.error(resp.message, { autoClose: 2500 });
        setVerifying(false);
      }
    });
  }, []);

  useEffect(() => { if (Number.isFinite(orderId)) load(orderId); }, [orderId, load]);

  const breadcrumbs: BreadcrumbsItem[] = [
    { name: 'Órdenes de Recolección', url: '/selection-orders', isActive: false },
    { name: `Detalle Orden #${orderId}`, url: `/selection-orders/${orderId}`, isActive: true },
  ];

  const formatDate = (d?: string | null) => {
    if (!d) return '';
    try { const date = new Date(d); return Number.isNaN(date.getTime()) ? (d as string) : date.toLocaleDateString(); }
    catch { return d as string; }
  };

  const createdByName =
    (order as any).created_by_name ||
    (order as any).created_by_fullname ||
    [ (order as any)?.created_by?.first_name, (order as any)?.created_by?.last_name ].filter(Boolean).join(' ') ||
    (order as any)?.created_by?.username || '';

  const updatedByName =
    (order as any).updated_by_name ||
    (order as any).updated_by_fullname ||
    [ (order as any)?.updated_by?.first_name, (order as any)?.updated_by?.last_name ].filter(Boolean).join(' ') ||
    (order as any)?.updated_by?.username || '';

  if (verifying) {
    return (
      <DefaultCard>
        <LazyLoading height={300} />
      </DefaultCard>
    );
  }

  return (
    <>
      <Breadcrumbs pageSection={`Detalle Orden #${orderId}`} breadcrumbs={breadcrumbs} />

      {/* === Fila con 2 contenedores separados === */}
      <div className="row">
        {/* Contenedor izquierdo: Datos Generales (solo lectura) */}
        <div className="col-12 col-lg-8">
          <DefaultCard>
            <h5 className="mb-3">Datos Generales</h5>
            <SelectionOrderHeaderForm
              fetching={!!fetchingShowSelectionOrder}
              order={order}
              setOrder={setOrder}
              societies={[]}
              units={[]}
              statuses={[]}
              errorFields={undefined}
              onCancel={() => window.history.back()}
              onSave={() => undefined}
              readOnly
              hideActions
            />
          </DefaultCard>
        </div>

        {/* Contenedor derecho: Datos del creador */}
        <div className="col-12 col-lg-4">
          <DefaultCard>
            <h5 className="mb-3">Datos del creador</h5>
            <div className="mb-2">
              <label className="form-label">Creado por</label>
              <input className="form-control" value={createdByName} disabled />
            </div>
            <div className="mb-2">
              <label className="form-label">Fecha de creación</label>
              <input className="form-control" value={formatDate((order as any).created_at)} disabled />
            </div>
            <div className="mb-2">
              <label className="form-label">Editado por</label>
              <input className="form-control" value={updatedByName} disabled />
            </div>
            <div className="mb-2">
              <label className="form-label">Fecha de actualización</label>
              <input className="form-control" value={formatDate((order as any).updated_at)} disabled />
            </div>
            <hr />
            <div className="mb-2">
              <label className="form-label">Estado actual</label>
              <input className="form-control" value={order.status_label ?? order.status ?? ''} disabled />
            </div>
            <div className="mb-2">
              <label className="form-label">Activo</label>
              <input className="form-control" value={order.is_active ? 'Sí' : 'No'} disabled />
            </div>
          </DefaultCard>
        </div>
      </div>

      {/* Contenedor inferior: pestañas con tablas (solo lectura) */}
      <DefaultCard>
        <Tabs defaultActiveKey="batches" id="selection-order-tabs" className="mb-3">
          <Tab eventKey="batches" title="Lotes asignados">
            <div className="col-12">
              <OrderBatchesTab
                orderId={orderId}
                pendingRows={(order.pending_batches as SelectionOrderBatchRow[]) || []}
                destinationRows={order.destination_batches || []}
                readOnly
                onChanged={() => undefined}
              />
            </div>
          </Tab>
          <Tab eventKey="collectors" title="Usuarios asignados">
            <div className="col-12">
              <OrderCollectorsTab
                orderId={orderId}
                rows={(order.collectors as SelectionOrderCollectorRow[]) || []}
                readOnly
                onChanged={() => undefined}
              />
            </div>
          </Tab>
        </Tabs>
      </DefaultCard>
    </>
  );
};

export default SelectionOrdersShow;